<script setup lang="ts">
import { useAuthStore } from '@/store/auth-store';
import { computed, onMounted, shallowRef } from 'vue';
import { VOtpInput } from 'vuetify/lib/components';
import { store as vuex } from '@/store/store';
import { required, useRules } from '@/composables/vuetify-validation';
import { useRouter } from '@/router/router';
import i18n from '@/localisation/i18n';
import { errorString } from '@/utils/helpers/rest-response';

const router = useRouter();
const authStore = useAuthStore();

const props = withDefaults(
  defineProps<{
    loginEmail?: string;
    loginPassword?: string;
  }>(),
  { loginEmail: '', loginPassword: '' }
);

const clientConfig = computed(() => vuex.state.clientConfig!);

const cmpOTPInput = shallowRef<null | { focus(): void }>(null);
const otp = shallowRef('');
const countProcessing = shallowRef(0);
const showSuccess = shallowRef(false);

function focusOtpInput(): void {
  cmpOTPInput.value?.focus();
}

onMounted(focusOtpInput);

const submitForm = () =>
  validateAndSubmit(async () => {
    // success, inform user and then continue
    showSuccess.value = true;

    await router.replace({ name: 'dashboard' });
  });

const { validateAndSubmit, rules } = useRules<{ otp: string }>({
  otp: {
    rules: [
      (v) => required(v, 'Please enter a code to continue.'),
      (v, { isSubmitting }) =>
        !isSubmitting || (v && v.length === 6) || 'Please enter a 6 digit code.',
      (v) => {
        if (v?.length !== 6) return true;
        ++countProcessing.value;
        return authStore
          .login({
            email: props.loginEmail,
            password: props.loginPassword,
            otpCode: v,
          })
          .then((result) => {
            if (result === undefined) return '';

            if (result.success) return true;

            focusOtpInput();
            return i18n.tc(result.error);
          })
          .catch((err) => {
            focusOtpInput();
            return errorString(err);
          })
          .finally(() => --countProcessing.value);
      },
    ],
  },
});
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="600"
    origin="center center"
    overlay-color="secondary"
    overlay-opacity="0.25"
    persistent
    transition="scale-transition"
    :value="true"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">{{ $t('loginTitle2FA') }}</span>
        </VCardTitle>

        <VCardText>
          <VContainer>
            <VRow class="text-center">
              <VCol class="pt-6">
                <p class="text-center">
                  <span class="text-body-1">
                    Your account is secured with Two-Factor Authentication.
                  </span>
                  <br />
                  <span v-if="clientConfig && clientConfig.tfaResetByEmail">
                    Lost your device? Request a reset
                    <RouterLink :to="{ name: 'lost-device', params: { lostEmail: loginEmail } }">
                      here
                    </RouterLink>
                  </span>
                </p>
              </VCol>
            </VRow>

            <VRow>
              <VCol class="pt-8">
                <p>Enter the 6-digit code from your app:</p>
                <VOtpInput
                  :ref="(r) => (cmpOTPInput = r)"
                  v-model="otp"
                  :disabled="!!countProcessing"
                  v-bind="rules.otp"
                  length="6"
                  plains
                  type="number"
                />
                <label class="error--text">{{ rules.otp.errorMessages.join('\n') }}</label>
              </VCol>
            </VRow>

            <VRow v-if="showSuccess">
              <VCol class="pa-0 px-1 col-6 offset-3">
                <div class="v-alert v-alert--dense text--primary text-body-2 text-center success">
                  Login successful!
                </div>
              </VCol>
            </VRow>
          </VContainer>
        </VCardText>

        <VCard-actions>
          <VBtn class="d-none d-sm-flex" color="secondary" text :to="{ name: 'login' }">
            {{ $t('dialogs.goBackButton') }}
          </VBtn>
          <VSpacer />
          <VBtn
            class="px-6"
            color="primary"
            :disabled="!!countProcessing"
            :loading="!!countProcessing"
            type="submit"
            @click="submitForm"
          >
            {{ $t('dialogs.continueButton') }}
          </VBtn>
        </VCard-actions>
      </VCard>
    </VForm>
  </VDialog>
</template>
